<script setup lang="ts">
/**
 * SG Incorporation (Pre) — 실제 설립 업무 흐름 Stage 스텝퍼.
 * 1 CDD → 2 Engagement → 3 Officer consents → 4 Shareholders/Controllers → 5 Constitution
 * Form 45 / 45B / Notices / CS Profile: N copies via toolbar + DocScope.
 * v-show 로 단계 전환 → 입력값 보존.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import type { Component } from 'vue';
import DocScope from './cdd/DocScope.vue';
import SgConstitutionCoverDoc from './SgConstitutionCoverDoc.vue';
import SgEngagementLetterDoc from './SgEngagementLetterDoc.vue';
import SgForm45Doc from './SgForm45Doc.vue';
import SgForm45BDoc from './SgForm45BDoc.vue';
import SgNoticeControllerCorporateDoc from './SgNoticeControllerCorporateDoc.vue';
import SgNoticeControllerIndividualDoc from './SgNoticeControllerIndividualDoc.vue';
import SgCorporateShareholderProfileDoc from './SgCorporateShareholderProfileDoc.vue';
import SgClientAcceptanceDoc from './SgClientAcceptanceDoc.vue';
import SgAcraRiskAssessmentDoc from './SgAcraRiskAssessmentDoc.vue';
import type { SgIncPreIncludes } from '../../mappers/mapSgIncorporationToPreValues';
import { useCddFill } from './cdd/useCddFill';
import { syncCafToStageDocs } from './cdd/syncCafToStageDocs';
import StudioStageNav from '@/features/startTask/preset_task/shared/preset/components/StudioStageNav.vue';

const EM = '\u2014';

const props = withDefaults(
  defineProps<{
    /** Setup Entity Yes/No — false 면 해당 문서·Stage 미생성 */
    includes?: Partial<SgIncPreIncludes>;
  }>(),
  {
    includes: () => ({}),
  },
);

const emit = defineEmits<{
  (e: 'sync-includes', partial: Partial<SgIncPreIncludes>): void;
}>();

const fill = useCddFill();

const inc = computed<SgIncPreIncludes>(() => ({
  directors: props.includes?.directors !== false,
  secretary: props.includes?.secretary !== false,
  shareholders: props.includes?.shareholders !== false,
  controllers: props.includes?.controllers !== false,
  engagement: props.includes?.engagement !== false,
}));

type ListKey = 'f45' | 'f45b' | 'corp' | 'ind' | 'cs' | 'eng';
type DocListKey = Exclude<ListKey, 'eng'>;

type Slot =
  | { kind: 'fixed'; icon: string; name: string; comp: Component; key: string; a4?: boolean }
  | {
      kind: 'repeat';
      icon: string;
      name: string;
      comp: Component;
      scope: string;
      id: number;
      listKey: DocListKey;
    };

interface StageDef {
  no: number;
  /** Stage 본문 제목 */
  en: string;
  /** 스텝퍼용 짧은 라벨 (한 줄 유지) */
  short: string;
  /** stage 안 Add 버튼 (없으면 툴바 숨김) */
  tools?: { key: ListKey; label: string }[];
  buildSlots: () => Slot[];
}

const form45Ids = ref<number[]>([1]);
const form45bIds = ref<number[]>([1]);
const corpCtrlIds = ref<number[]>([1]);
const indCtrlIds = ref<number[]>([1]);
const csProfileIds = ref<number[]>([1]);

let seq = { f45: 1, f45b: 1, corp: 1, ind: 1, cs: 1 };

/**
 * 사용자가 × 로 지운 문서 부수.
 *   CDD 동기화(runCafStageSync → applyList)가 이사/주주 데이터를 보고 슬롯을 합집합으로
 *   복원하기 때문에, 기록해 두지 않으면 삭제 직후 바로 되살아난다(= 삭제가 안 되는 현상).
 *   다시 "+" 로 추가하면 해당 번호는 목록에서 빠진다.
 */
const removedIds: Record<DocListKey, Set<number>> = {
  f45: new Set<number>(),
  f45b: new Set<number>(),
  corp: new Set<number>(),
  ind: new Set<number>(),
  cs: new Set<number>(),
};

/**
 * 비어 있으면 기본 1부를 시드한다.
 *   단 사용자가 × 로 마지막 부까지 지운 섹션(removedIds 에 기록됨)은 **되살리지 않는다**.
 *   (이 가드가 없으면 "마지막 한 부는 지워지지 않는" 현상이 생긴다)
 */
function seedIfEmpty(refList: typeof form45Ids, key: DocListKey) {
  if (refList.value.length) return;
  if (removedIds[key].size) return;
  refList.value = [1];
}

function listOf(key: DocListKey) {
  if (key === 'f45') return form45Ids;
  if (key === 'f45b') return form45bIds;
  if (key === 'corp') return corpCtrlIds;
  if (key === 'ind') return indCtrlIds;
  return csProfileIds;
}

/** Setup Entity 에서 No 였던 문서도 Pre Task 에서 + 로 추가 가능 */
function add(key: ListKey) {
  if (key === 'eng') {
    emit('sync-includes', { engagement: true });
    return;
  }
  const list = listOf(key);
  const wasEmpty = !list.value.length;

  const patch: Partial<SgIncPreIncludes> = {};
  if (key === 'f45') patch.directors = true;
  if (key === 'f45b') patch.secretary = true;
  if (key === 'cs') patch.shareholders = true;
  if (key === 'corp' || key === 'ind') patch.controllers = true;
  if (Object.keys(patch).length) emit('sync-includes', patch);

  // include 플래그 watch 가 [1] 을 시드했을 수 있음 — 첫 추가는 중복 push 금지
  if (wasEmpty) {
    if (!list.value.length) {
      seq[key] = 1;
      list.value = [1];
      removedIds[key].delete(1); // 다시 추가 → 삭제 기록 해제
    }
    return;
  }
  seq[key] += 1;
  removedIds[key].delete(seq[key]);
  list.value.push(seq[key]);
}
function remove(key: DocListKey, id: number) {
  const list = listOf(key);
  // 삭제 기록 — sync 가 다시 채워 넣지 못하게 한다.
  removedIds[key].add(id);
  if (list.value.length <= 1) {
    // 마지막 부 삭제 = 해당 섹션 비움 (다시 + 로 추가 가능)
    list.value = [];
    return;
  }
  list.value = list.value.filter((x) => x !== id);
}
function countOf(key: DocListKey) {
  return listOf(key).value.length;
}

/**
 * Stage 는 Setup Yes/No 와 무관하게 항상 표시.
 * No 였으면 문서 0부 + 툴바로 추가. Yes 면 기본 1부.
 */
const activeStages = computed((): StageDef[] => {
  const i = inc.value;
  const stages: Omit<StageDef, 'no'>[] = [
    {
      en: 'Client Acceptance & Risk (CDD)',
      short: 'CDD & Risk',
      buildSlots: () => [
        {
          kind: 'fixed',
          key: 'client',
          icon: '\uD83D\uDCCB',
          name: 'Client Acceptance Form (CDD)',
          comp: SgClientAcceptanceDoc,
        },
        {
          kind: 'fixed',
          key: 'risk',
          icon: '\u26A0\uFE0F',
          name: 'ACRA Risk Assessment Form (CDD)',
          comp: SgAcraRiskAssessmentDoc,
        },
      ],
    },
    {
      en: 'Engagement Letter',
      short: 'Engagement',
      tools: i.engagement ? [] : [{ key: 'eng', label: '+ Add Engagement Letter' }],
      buildSlots: () =>
        i.engagement
          ? [
              {
                kind: 'fixed',
                key: 'engagement',
                icon: '\u2709\uFE0F',
                name: 'Engagement Letter',
                comp: SgEngagementLetterDoc,
              },
            ]
          : [],
    },
    {
      en: 'Director & Secretary Consents',
      short: 'Consents',
      tools: [
        { key: 'f45', label: '+ Form 45' },
        { key: 'f45b', label: '+ Form 45B' },
      ],
      buildSlots: () => {
        const out: Slot[] = [];
        for (const id of form45Ids.value) {
          out.push({
            kind: 'repeat',
            icon: '\uD83D\uDD8A\uFE0F',
            name: `Form 45 ${EM} Consent to Act as Director`,
            comp: SgForm45Doc,
            scope: `f45_${id}/`,
            id,
            listKey: 'f45',
          });
        }
        for (const id of form45bIds.value) {
          out.push({
            kind: 'repeat',
            icon: '\uD83D\uDD8A\uFE0F',
            name: `Form 45B ${EM} Consent to Act as Secretary`,
            comp: SgForm45BDoc,
            scope: `f45b_${id}/`,
            id,
            listKey: 'f45b',
          });
        }
        return out;
      },
    },
    {
      en: 'Shareholders & Controllers',
      short: 'SH & Controllers',
      tools: [
        { key: 'cs', label: '+ Corporate Shareholder Profile' },
        { key: 'corp', label: '+ Corporate Controller Notice' },
        { key: 'ind', label: '+ Individual Controller Notice' },
      ],
      buildSlots: () => {
        const out: Slot[] = [];
        for (const id of csProfileIds.value) {
          out.push({
            kind: 'repeat',
            icon: '\uD83C\uDFDB\uFE0F',
            name: 'Corporate Shareholder Profile (CDD)',
            comp: SgCorporateShareholderProfileDoc,
            scope: `cs_${id}/`,
            id,
            listKey: 'cs',
          });
        }
        for (const id of corpCtrlIds.value) {
          out.push({
            kind: 'repeat',
            icon: '\uD83C\uDFE2',
            name: `Notice for Controllers 386AG(2)(a) ${EM} Corporate`,
            comp: SgNoticeControllerCorporateDoc,
            scope: `ctrlc_${id}/`,
            id,
            listKey: 'corp',
          });
        }
        for (const id of indCtrlIds.value) {
          out.push({
            kind: 'repeat',
            icon: '\uD83D\uDC64',
            name: `Notice for Controllers 386AG(2)(a) ${EM} Individual`,
            comp: SgNoticeControllerIndividualDoc,
            scope: `ctrli_${id}/`,
            id,
            listKey: 'ind',
          });
        }
        return out;
      },
    },
    {
      en: 'Constitution',
      short: 'Constitution',
      buildSlots: () => [
        {
          kind: 'fixed',
          key: 'constitution',
          icon: '\uD83D\uDCDC',
          name: 'Constitution Cover Page',
          comp: SgConstitutionCoverDoc,
          a4: true,
        },
      ],
    },
  ];

  return stages.map((st, idx) => ({ ...st, no: idx + 1 }));
});

const currentStage = ref(1);

watch(
  inc,
  (i) => {
    // Setup No → 빈 목록(툴바로 추가). Yes 이고 비어 있으면 기본 1부.
    if (!i.directors) form45Ids.value = [];
    else seedIfEmpty(form45Ids, 'f45');

    if (!i.secretary) form45bIds.value = [];
    else seedIfEmpty(form45bIds, 'f45b');

    if (!i.controllers) {
      corpCtrlIds.value = [];
      indCtrlIds.value = [];
    } else {
      seedIfEmpty(corpCtrlIds, 'corp');
      seedIfEmpty(indCtrlIds, 'ind');
    }

    if (!i.shareholders) csProfileIds.value = [];
    else seedIfEmpty(csProfileIds, 'cs');
  },
  { deep: true, immediate: true },
);

const view = computed(() => {
  let n = 0;
  return activeStages.value.map((st) => ({
    no: st.no,
    en: st.en,
    short: st.short,
    tools: st.tools || [],
    docs: st.buildSlots().map((d) => ({ ...d, no: (n += 1) })),
  }));
});

const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));
const totalStages = computed(() => activeStages.value.length);

watch(totalStages, (n) => {
  if (currentStage.value > n) currentStage.value = Math.max(1, n);
});

function go(n: number) {
  if (n >= 1 && n <= totalStages.value) currentStage.value = n;
}
/** Stage 루트 요소 — StudioStageNav 가 그 안의 빈 칸을 찾는다 */
const stageEls: Record<number, HTMLElement | null> = {};
/** 스테퍼 클릭도 같은 빈 칸 확인을 거친다 */
const stageNav = ref<InstanceType<typeof StudioStageNav> | null>(null);
function setStageEl(no: number, el: unknown) {
  stageEls[no] = (el as HTMLElement | null) || null;
}

type CafApi = {
  restoreRows?: (caf: Record<string, number[]>) => void;
  gatherRows?: () => Record<string, number[]>;
};
const cafRef = ref<CafApi | null>(null);
function setCafRef(el: unknown) {
  cafRef.value = (el as CafApi) || null;
}

type LocalState = {
  form45Ids?: number[];
  form45bIds?: number[];
  corpCtrlIds?: number[];
  indCtrlIds?: number[];
  csProfileIds?: number[];
  /** 사용자가 지운 부수 (복원 후 sync 가 되살리지 않도록) */
  removedIds?: Record<string, number[]>;
  caf?: Record<string, number[]>;
  currentStage?: number;
};

const pendingLocal = ref<LocalState | null>(null);
let mounted = false;

function gatherLocalState() {
  return {
    form45Ids: [...form45Ids.value],
    form45bIds: [...form45bIds.value],
    corpCtrlIds: [...corpCtrlIds.value],
    indCtrlIds: [...indCtrlIds.value],
    csProfileIds: [...csProfileIds.value],
    removedIds: Object.fromEntries(
      (Object.keys(removedIds) as DocListKey[]).map((k) => [k, [...removedIds[k]]]),
    ) as Record<string, number[]>,
    caf: cafRef.value?.gatherRows?.() || null,
    currentStage: currentStage.value,
  };
}

function applyLocalState(state: LocalState) {
  if (!state) return;
  // 삭제 기록 먼저 복원 — 이후 sync 가 지운 부수를 되살리지 않도록.
  if (state.removedIds) {
    for (const [k, ids] of Object.entries(state.removedIds)) {
      const set = removedIds[k as DocListKey];
      if (!set) continue;
      set.clear();
      for (const n of ids || []) set.add(Number(n));
    }
  }
  const apply = (refList: typeof form45Ids, ids: number[] | undefined, key: DocListKey) => {
    if (ids === undefined) return;
    refList.value = ids.length ? [...ids] : [];
    if (ids.length) seq[key] = Math.max(...ids, seq[key]);
  };
  apply(form45Ids, state.form45Ids, 'f45');
  apply(form45bIds, state.form45bIds, 'f45b');
  apply(corpCtrlIds, state.corpCtrlIds, 'corp');
  apply(indCtrlIds, state.indCtrlIds, 'ind');
  apply(csProfileIds, state.csProfileIds, 'cs');
  if (state.currentStage && state.currentStage >= 1 && state.currentStage <= totalStages.value) {
    currentStage.value = state.currentStage;
  }
  if (state.caf) {
    const caf = state.caf;
    const tryRestore = (attempt: number) => {
      if (cafRef.value?.restoreRows) {
        cafRef.value.restoreRows(caf);
        return;
      }
      if (attempt >= 8) return;
      void nextTick(() => tryRestore(attempt + 1));
    };
    void nextTick(() => tryRestore(0));
  }
}

function restoreLocalState(state: LocalState) {
  if (!state) return;
  pendingLocal.value = state;
  if (mounted) applyLocalState(state);
}

onMounted(() => {
  mounted = true;
  if (pendingLocal.value) applyLocalState(pendingLocal.value);
  scheduleCafStageSync();
});

// ─── 문서 확대 (시니어 가독성) — localStorage 유지 ───────────────
const ZOOM_KEY = 'sgIncPre.docZoom';
const ZOOM_MIN = 100;
const ZOOM_MAX = 175;
const ZOOM_STEP = 25;
function clampZoom(n: number) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP));
}
function readStoredZoom(): number {
  try {
    const n = Number(localStorage.getItem(ZOOM_KEY));
    if (Number.isFinite(n)) return clampZoom(n);
  } catch {
    /* ignore */
  }
  return 100;
}
const docZoom = ref(readStoredZoom());
const canZoomIn = computed(() => docZoom.value < ZOOM_MAX);
const canZoomOut = computed(() => docZoom.value > ZOOM_MIN);
function setDocZoom(n: number) {
  docZoom.value = clampZoom(n);
  try {
    localStorage.setItem(ZOOM_KEY, String(docZoom.value));
  } catch {
    /* ignore */
  }
}
function zoomIn() {
  setDocZoom(docZoom.value + ZOOM_STEP);
}
function zoomOut() {
  setDocZoom(docZoom.value - ZOOM_STEP);
}
function zoomReset() {
  setDocZoom(100);
}

/** CDD 챗봇/입력 → Form45 / 45B / CS / Notices 자동 복사본·값 연동 */
let syncTimer: ReturnType<typeof setTimeout> | null = null;
function applyList(refList: typeof form45Ids, ids: number[], key: DocListKey) {
  // 사용자가 지운 부수는 다시 넣지 않는다.
  const allowed = ids.filter((id) => !removedIds[key].has(id));
  if (!allowed.length) return;
  const merged = [...new Set([...refList.value, ...allowed])].sort((a, b) => a - b);
  refList.value = merged;
  seq[key] = Math.max(seq[key], ...merged);
}
function runCafStageSync() {
  const result = syncCafToStageDocs(
    fill.values as Record<string, string>,
    (k, v) => fill.setValue(k, v),
    {
      // 인물(dir/sec) 없어도 떠 있는 Form45/Notice 슬롯에 회사명 반영
      form45Ids: [...form45Ids.value],
      form45bIds: [...form45bIds.value],
      corpCtrlIds: [...corpCtrlIds.value],
      indCtrlIds: [...indCtrlIds.value],
      csProfileIds: [...csProfileIds.value],
    },
  );
  applyList(form45Ids, result.lists.form45Ids, 'f45');
  applyList(form45bIds, result.lists.form45bIds, 'f45b');
  applyList(csProfileIds, result.lists.csProfileIds, 'cs');
  applyList(indCtrlIds, result.lists.indCtrlIds, 'ind');
  applyList(corpCtrlIds, result.lists.corpCtrlIds, 'corp');
  if (Object.keys(result.includes).length) emit('sync-includes', result.includes);
}
function scheduleCafStageSync() {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    runCafStageSync();
  }, 80);
}

fill.setAfterChangeHandler(() => scheduleCafStageSync());

watch(
  () =>
    Object.keys(fill.values)
      .filter((k) =>
        /^(dir|sec|ictrl|cctrl)\d+_/.test(k) ||
        /^(ihc_|upc_|has_corporate)/.test(k) ||
        k === 'entity_name' ||
        k === 'company_name' ||
        k === 'proposed_company_name' ||
        k === 'entity_uen' ||
        k === 'company_registration_no' ||
        k === 'entity_registration_no' ||
        /\/company_name$/.test(k) ||
        /\/company_registration_no$/.test(k),
      )
      .map((k) => `${k}=${fill.values[k]}`)
      .join('|'),
  () => scheduleCafStageSync(),
);

// + Form 45 등으로 슬롯이 늘면 회사명을 새 스코프에 즉시 반영
watch(
  () =>
    [form45Ids.value, form45bIds.value, corpCtrlIds.value, indCtrlIds.value]
      .map((ids) => ids.join(','))
      .join('|'),
  () => scheduleCafStageSync(),
);

defineExpose({ gatherLocalState, restoreLocalState, syncFromCdd: runCafStageSync });
</script>

<template>
  <div class="allot-pkg sg-inc-pkg">
    <!-- Stage stepper — full width -->
    <div class="stepper">
      <button
        v-for="st in view"
        :key="st.no"
        type="button"
        class="step"
        :class="{ active: currentStage === st.no, done: currentStage > st.no }"
        :title="`Stage ${st.no} · ${st.en}`"
        @click="stageNav?.requestGo(st.no)"
      >
        <span class="step-no">{{ currentStage > st.no ? '\u2713' : st.no }}</span>
        <span class="step-txt">
          <b>Stage {{ st.no }}</b>
          <small>{{ st.short }}</small>
        </span>
      </button>
    </div>

    <!-- Document zoom — separate row (not scaled with paper) -->
    <div class="zoom-bar">
      <div class="doc-zoom" role="group" aria-label="Larger text">
        <button
          type="button"
          class="doc-zoom-btn"
          :disabled="!canZoomOut"
          title="Smaller text"
          @click="zoomOut"
        >
          A−
        </button>
        <button type="button" class="doc-zoom-pct" title="Reset size" @click="zoomReset">
          {{ docZoom }}%
        </button>
        <button
          type="button"
          class="doc-zoom-btn"
          :disabled="!canZoomIn"
          title="Larger text"
          @click="zoomIn"
        >
          A+
        </button>
      </div>
    </div>

    <!-- Stages (v-show keeps inputs mounted) -->
    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <section
        v-for="st in view"
        :key="st.no"
        v-show="currentStage === st.no"
        :ref="(el) => setStageEl(st.no, el)"
        class="stage"
      >
        <div class="stage-head">
          <span class="stage-badge">Stage {{ st.no }}</span>
          <span class="stage-t">{{ st.en }}</span>
        </div>

        <div v-if="st.tools.length" class="sg-toolbar">
          <span class="sg-toolbar-lbl">Add document</span>
          <button
            v-for="tool in st.tools"
            :key="tool.key"
            type="button"
            class="sg-tool"
            @click="add(tool.key)"
          >
            {{ tool.label }}
          </button>
        </div>

        <div v-if="!st.docs.length" class="sg-empty">
          No documents in this stage yet. Use <b>Add document</b> above to include ones skipped in Setup Entity.
        </div>

        <div
          v-for="d in st.docs"
          :key="d.kind === 'fixed' ? d.key : d.scope"
          class="pkg-doc"
        >
          <div class="pkg-doclabel">
            <span class="ic">{{ d.icon }}</span>
            <span class="nm">{{ d.name }}</span>
            <template v-if="d.kind === 'repeat'">
              <span class="copy">#{{ d.id }}</span>
              <button
                type="button"
                class="sg-rm"
                title="Remove copy"
                @click="remove(d.listKey, d.id)"
              >
                &times;
              </button>
            </template>
            <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
          </div>

          <div class="simple-frame" :class="{ 'a4-sheet': d.kind === 'fixed' && d.a4 }">
            <DocScope v-if="d.kind === 'repeat'" :scope="d.scope">
              <component :is="d.comp" />
            </DocScope>
            <SgClientAcceptanceDoc
              v-else-if="d.kind === 'fixed' && d.key === 'client'"
              :ref="(el) => setCafRef(el)"
            />
            <component v-else :is="d.comp" />
          </div>
        </div>
      </section>
    </div>

    <!-- 공통 Stage 이동 — 다음 Stage 로 갈 때 빈 칸 확인(Yes 넘어감 / No 빈 칸 빨간 표시) -->
    <StudioStageNav
      ref="stageNav"
      :current="currentStage"
      :total="totalStages"
      :get-stage-el="() => stageEls[currentStage]"
      @go="go"
    />
  </div>
</template>

<style scoped>
.allot-pkg { color: #111; }

.stepper {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  width: 794px;
  max-width: 100%;
  margin: 0 auto 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(20, 30, 60, 0.06);
  box-sizing: border-box;
}
.zoom-bar {
  display: flex;
  justify-content: flex-end;
  width: 794px;
  max-width: 100%;
  margin: 0 auto 18px;
  box-sizing: border-box;
}
.doc-zoom {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f2f4f6;
  border-radius: 10px;
  padding: 2px;
}
.doc-zoom-btn,
.doc-zoom-pct {
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 800;
  color: #4b5563;
  border-radius: 8px;
  line-height: 1;
}
.doc-zoom-btn {
  min-width: 36px;
  height: 32px;
  font-size: 15px;
}
.doc-zoom-btn:hover:not(:disabled) {
  background: #fff;
  color: #191f28;
}
.doc-zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.doc-zoom-pct {
  min-width: 52px;
  height: 32px;
  font-size: 12px;
  color: #3182f6;
}
.doc-zoom-pct:hover {
  background: #fff;
}
.paper-zoom {
  width: 794px;
  max-width: 100%;
  margin: 0 auto;
}
.step {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 10px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: #f6f8fc;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
}
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #c9d3e4;
  color: #fff;
  font-weight: 800;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; }
.step-txt small {
  font-size: 10px;
  color: #7a869c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stage { width: 100%; max-width: 100%; margin: 0 auto; }
.stage-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 18px;
  padding: 4px 2px 14px;
  border-bottom: 1px solid #eef1f6;
}
.stage-badge {
  flex: none;
  background: #1b64da;
  color: #fff;
  font-weight: 800;
  font-size: 12.5px;
  padding: 5px 12px;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }

.sg-toolbar {
  width: 100%;
  margin: 0 0 22px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 14px 16px;
  border: 1px solid #d7e0f0;
  border-radius: 12px;
  background: #f7f9fd;
  box-sizing: border-box;
}
.sg-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; margin-right: 6px; }
.sg-tool {
  border: 1px solid #1b84ff;
  background: #fff;
  color: #1b84ff;
  border-radius: 999px;
  padding: 7px 13px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.sg-tool:hover { background: #eaf3ff; }

.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 12px;
}
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; letter-spacing: 0.01em; }
.pkg-doclabel .copy {
  font-size: 10.5px;
  font-weight: 800;
  color: #1b84ff;
  background: #eaf3ff;
  border-radius: 999px;
  padding: 2px 8px;
}
.pkg-doclabel .no {
  margin-left: auto;
  font-size: 10.5px;
  font-weight: 800;
  color: #9aa6bd;
  white-space: nowrap;
}
.sg-rm {
  border: none;
  background: transparent;
  color: #c0392b;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;
}
.simple-frame {
  width: 100%;
  background: #fff;
  border: 1px solid #e2e5ea;
  box-shadow: 0 4px 20px rgba(20, 30, 60, 0.09);
  padding: 32px;
  box-sizing: border-box;
  overflow: hidden;
  font-size: 12px;
}
/* Constitution Cover 등 한 장짜리 — 다른 프리셋과 동일 A4 */
.simple-frame.a4-sheet {
  width: 210mm;
  min-height: 297mm;
  max-width: 100%;
  margin: 0 auto;
  padding: 18mm 20mm;
  box-shadow: 0 4px 24px rgba(20, 30, 60, 0.12);
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.simple-frame.a4-sheet > .allot-doc {
  flex: 1 1 auto;
  width: 100%;
}
.sg-empty {
  width: 100%;
  margin: 8px 0 18px;
  padding: 16px 18px;
  border: 1px dashed #c5d0e0;
  border-radius: 12px;
  background: #f7f9fc;
  color: #5b6b82;
  font-size: 13px;
  line-height: 1.45;
  box-sizing: border-box;
}

</style>


<style>
.paper:has(.sg-inc-pkg) {
  width: 794px !important;
  max-width: 100% !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
.sg-inc-pkg .allot-doc {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.sg-inc-pkg .cdd-tbl {
  width: 100% !important;
  table-layout: fixed !important;
  box-sizing: border-box;
}
.sg-inc-pkg .cdd-tbl td,
.sg-inc-pkg .cdd-tbl th {
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-break: normal;
  vertical-align: top;
}
.sg-inc-pkg .cdd-lcell { width: 32%; }
.sg-inc-pkg .lv-field {
  display: inline-flex !important;
  max-width: 100%;
  box-sizing: border-box;
  white-space: normal !important;
  flex-wrap: wrap;
  align-items: baseline;
  vertical-align: baseline;
}
.sg-inc-pkg .lv-field .lv-text {
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
}
.sg-inc-pkg .risk-tbl col.yn { width: 52px; }
.sg-inc-pkg .risk-tbl .yn-cell {
  width: 52px;
  text-align: center;
  vertical-align: middle;
}
.sg-inc-pkg .risk-tbl thead th.yn-h {
  width: 52px;
  text-align: center;
  white-space: nowrap;
}
.sg-inc-pkg .fee-tbl col.fee { width: 260px; }
.sg-inc-pkg .fee-tbl td.fee-cell {
  width: 260px;
  white-space: nowrap;
}
</style>
